import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/services/api/auth'
import { auth, googleProvider, signInWithGooglePopup, getIdToken } from '@/lib/firebase'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth'
import type { User, LoginCredentials, RegisterData } from '@/types/auth'
import type { ActiveContext } from '@/types/merchant'
import { track } from '@/services/analytics'
import i18n from '@/i18n/index'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const merchantRoles = ref<{ merchantId: string; merchantName: string; merchantSlug: string; role: string }[]>([])
  const activeContext = ref<ActiveContext | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isOrganizer = computed(() => user.value?.isOrganizer || false)
  const isAdmin = computed(() => user.value?.role === 'ADMIN')
  const isVerified = computed(() => user.value?.isVerified || false)

  /** Vào khu /organizer: ADMIN, ORGANIZER/KYC, hoặc tài khoản FULL đã xác thực */
  const canAccessOrganizerArea = computed(() => {
    const u = user.value
    if (!u) return false
    if (u.role === 'ADMIN') return true
    if (u.role === 'ORGANIZER' || u.isOrganizer) return true
    if (u.accountType === 'FULL') return true
    return false
  })

  /** Sidebar đầy đủ: Tổng quan, chiến dịch, báo cáo, ... — không áp dụng cho user FULL thường */
  const hasFullOrganizerNav = computed(() => {
    const u = user.value
    if (!u) return false
    if (u.role === 'ADMIN') return true
    if (u.role === 'ORGANIZER' || u.isOrganizer) return true
    return false
  })

  const t = (key: string) => i18n.global.t(key) as string

  // Actions
  const login = async (credentials: LoginCredentials) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.login(credentials)

      user.value = response.user
      token.value = response.access_token
      merchantRoles.value = response.merchantRoles || []
      track('auth_login', { provider: 'local' })

      // Store in localStorage
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))
      localStorage.setItem('merchantRoles', JSON.stringify(response.merchantRoles || []))

      return response
    } catch (err: any) {
      const data = err.response?.data
      const errorCode = data?.errorCode

      if (errorCode === 'AUTH.INVALID_CREDENTIALS') {
        error.value = t('auth.error_invalid_credentials')
      } else {
        const msg = typeof data?.message === 'object' ? data.message.message : data?.message
        error.value = msg || t('auth.login_failed')
      }
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Firebase: Google popup sign-in → exchange Firebase ID token with backend
  const firebaseGoogleLogin = async () => {
    try {
      isLoading.value = true
      error.value = null

      const cred = await signInWithGooglePopup()
      const idToken = await getIdToken()

      const response = await authApi.firebaseLogin({ idToken })

      user.value = response.user
      token.value = response.access_token
      track('auth_register', { provider: 'local' })

      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || t('auth_store.login_google_firebase_failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Firebase: Email/password flows (optional helpers)
  const firebaseEmailLogin = async (email: string, password: string) => {
    try {
      isLoading.value = true
      error.value = null

      const res = await signInWithEmailAndPassword(auth, email, password)
      const idToken = await getIdToken()
      const response = await authApi.firebaseLogin({ idToken })

      user.value = response.user
      token.value = response.access_token
      track('auth_login', { provider: 'google' })
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || t('auth_store.login_failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const firebaseEmailRegister = async (email: string, password: string) => {
    try {
      isLoading.value = true
      error.value = null

      const res = await createUserWithEmailAndPassword(auth, email, password)
      const idToken = await getIdToken()
      const response = await authApi.firebaseLogin({ idToken })

      user.value = response.user
      token.value = response.access_token
      track('auth_login', { provider: 'web3' })
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || t('auth_store.register_failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData: RegisterData) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.register(userData)

      user.value = response.user
      token.value = response.access_token

      // Store in localStorage
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || t('auth_store.register_failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const googleLogin = async (idToken: string) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.googleLogin({ idToken })

      user.value = response.user
      token.value = response.access_token

      // Store in localStorage
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || t('auth_store.login_google_failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const web3Login = async (walletAddress: string, signature: string, message: string) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.web3Login({ walletAddress, signature, message })

      user.value = response.user
      token.value = response.access_token

      // Store in localStorage
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('user', JSON.stringify(response.user))

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || t('auth_store.login_web3_failed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const loginWithMagicToken = async (tokenStr: string) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await authApi.consumeMagicLink(tokenStr)

      user.value = response.user
      token.value = response.accessToken

      localStorage.setItem('token', response.accessToken)
      localStorage.setItem('user', JSON.stringify(response.user))

      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || t('auth_store.magic_link_invalid_or_expired')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    user.value = null
    token.value = null
    merchantRoles.value = []
    activeContext.value = null
    error.value = null
    track('auth_logout')

    // Clear localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('merchantRoles')
    localStorage.removeItem('activeContext')
    localStorage.removeItem('staff_token')
  }

  const initializeAuth = () => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    const storedRoles = localStorage.getItem('merchantRoles')
    const storedContext = localStorage.getItem('activeContext')

    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = JSON.parse(storedUser)
    }
    if (storedRoles) {
      merchantRoles.value = JSON.parse(storedRoles)
    }
    if (storedContext) {
      activeContext.value = JSON.parse(storedContext)
    }
  }

  const setActiveContext = async (merchantId: string) => {
    try {
      isLoading.value = true
      error.value = null
      
      const response = await authApi.setContext(merchantId)
      
      // Update token with the new contextToken
      token.value = response.contextToken
      localStorage.setItem('token', response.contextToken)
      
      // Update activeContext
      const contextData: ActiveContext = {
        activeRole: response.activeRole as any,
        activeMerchant: response.activeMerchant,
        activeBranch: response.activeBranch,
      }
      activeContext.value = contextData
      localStorage.setItem('activeContext', JSON.stringify(contextData))
      
      return response
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to switch context'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateProfile = (updatedUser: Partial<User>) => {
    if (user.value) {
      user.value = { ...user.value, ...updatedUser }
      localStorage.setItem('user', JSON.stringify(user.value))
    }
  }

  const clearError = () => {
    error.value = null
  }

  return {
    // State
    user,
    token,
    merchantRoles,
    activeContext,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    isOrganizer,
    isAdmin,
    isVerified,
    canAccessOrganizerArea,
    hasFullOrganizerNav,

    // Actions
    login,
    register,
    googleLogin,
    web3Login,
    loginWithMagicToken,
    logout,
    initializeAuth,
    setActiveContext,
    updateProfile,
    clearError,
    firebaseGoogleLogin,
    firebaseEmailLogin,
    firebaseEmailRegister,
  }
})




